// Bulk push, resolve, dismissal until the next run, and acceptance of agents' proposed
// dates or keeping the current ones, on a full list of actions:
// the bar once actions are ticked, the
// confirmation before anything is sent (each action and what happens to it, then those
// left alone and why), and how it went. See docs/Y2026-Q4/design-bulk-action-changes.md and
// docs/Y2026-Q4/design-bulk-accept-date-moves.md.
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useEffect, useRef, useState } from "react";

import {
  type BulkChangeOutcome,
  useBulkOperatorChange,
  useDismissActions,
} from "../api/hooks/useActionChanges";
import {
  type BulkChoice,
  type BulkPlan,
  type BulkRow,
  bulkBodies,
  planBulk,
} from "../utils/bulkActions";
import { agentDisplayLabel } from "../utils/runParticipants";
import { PushControl, ResolveControl } from "./ActionControls";
import styles from "./BulkActions.module.css";
import tableStyles from "./NextActionTable.module.css";

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? "" : "s"}`;

const VERB: Record<BulkChoice["kind"], string> = {
  push: "Push",
  resolve: "Resolve",
  accept_moves: "Accept",
  keep_moves: "Keep",
};

/** What a choice changes, counted: the actions, the proposed dates accepted, or the current
 * dates kept, as the single answer's Accept / Keep buttons say. */
const counted = (choice: BulkChoice, n: number) =>
  plural(
    n,
    choice.kind === "accept_moves"
      ? "proposed date"
      : choice.kind === "keep_moves"
        ? "current date"
        : "action",
  );

const where = (row: BulkRow, showProject: boolean): string =>
  showProject && row.projectName ? `${row.projectName} · ${row.agentLabel}` : row.agentLabel;

/** Above the table once actions are ticked: how many, Push for all of them, Accept the
 * agents' proposed dates among them or Keep their current dates, Resolve for all of them,
 * and a way to clear the selection. A choice goes to the confirmation, not the server. */
const BulkActionBar = ({
  count,
  moves,
  today,
  busy,
  onChoose,
  onDismiss,
  onClear,
}: {
  count: number;
  /** How many of them have an agent's proposal to move their date open. */
  moves: number;
  today: string;
  busy: boolean;
  onChoose: (choice: BulkChoice) => void;
  /** Dismiss every ticked action until its project's next run: no confirmation, as it
   * changes nothing an agent reads and each row undoes it. */
  onDismiss: () => void;
  onClear: () => void;
}) => {
  const [resolving, setResolving] = useState(false);
  return (
    <div className={styles.bar} role="toolbar" aria-label="Selected actions">
      <span className={styles.count}>{plural(count, "action")} selected</span>
      <PushControl
        target={{ scope: "each", onPush: (push) => onChoose({ kind: "push", push }) }}
        today={today}
        busy={busy}
      />
      {moves > 0 && (
        <button
          type="button"
          className={tableStyles.button}
          disabled={busy}
          onClick={() => onChoose({ kind: "accept_moves" })}
        >
          Accept proposed dates ({moves})
        </button>
      )}
      {moves > 0 && (
        <button
          type="button"
          className={tableStyles.button}
          disabled={busy}
          onClick={() => onChoose({ kind: "keep_moves" })}
        >
          Keep current dates ({moves})
        </button>
      )}
      <ResolveControl
        action={null}
        open={resolving}
        onOpenChange={setResolving}
        busy={busy}
        submitLabel={`Resolve ${plural(count, "action")}…`}
        onResolve={({ outcome, reason }) => {
          setResolving(false);
          onChoose({ kind: "resolve", outcome, reason });
        }}
      />
      <button type="button" className={tableStyles.button} disabled={busy} onClick={onDismiss}>
        Dismiss until next run
      </button>
      <button type="button" className={styles.clear} onClick={onClear}>
        Clear selection
      </button>
    </div>
  );
};

const BulkActionConfirm = ({
  choice,
  plan,
  showProject,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  /** What was chosen; null while the dialog is closed. */
  choice: BulkChoice | null;
  plan: BulkPlan;
  /** The table mixes projects, so each line names its project. */
  showProject: boolean;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  const ref = useRef<HTMLDialogElement | null>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (choice && !dialog.open) dialog.showModal();
    else if (!choice && dialog.open) dialog.close();
  }, [choice]);
  const verb = choice ? VERB[choice.kind] : "";
  const { planned, skipped } = plan;
  return (
    <dialog
      ref={ref}
      className={styles.dialog}
      aria-label={
        choice?.kind === "accept_moves"
          ? "Accept the proposed dates of the selected actions"
          : choice?.kind === "keep_moves"
            ? "Keep the current dates of the selected actions"
            : `${verb} the selected actions`
      }
      onCancel={(e) => {
        e.preventDefault();
        if (!busy) onCancel();
      }}
      // Chrome closes a modal on a second Escape even when its cancel was refused: while
      // it saves, it opens again.
      onClose={() => {
        if (!choice) return;
        if (busy) ref.current?.showModal();
        else onCancel();
      }}
    >
      {choice && (
        <div className={styles.body}>
          <h2 className={styles.heading}>
            {verb} {counted(choice, planned.length)}?
          </h2>
          {choice.kind === "resolve" && <p className={styles.reason}>“{choice.reason}”</p>}
          {planned.length > 0 && (
            <ul className={styles.list}>
              {planned.map(({ row, effect }) => (
                <li key={row.action.action_id} className={styles.item}>
                  <span className={styles.title}>{row.action.title}</span>
                  <span className={styles.meta}>{where(row, showProject)}</span>
                  <span className={styles.effect}>{effect}</span>
                </li>
              ))}
            </ul>
          )}
          {skipped.length > 0 && (
            <>
              <h3 className={styles.subheading}>Left as they are ({skipped.length})</h3>
              <ul className={styles.list}>
                {skipped.map(({ row, why }) => (
                  <li key={row.action.action_id} className={styles.item}>
                    <span className={styles.title}>{row.action.title}</span>
                    <span className={styles.meta}>{where(row, showProject)}</span>
                    <span className={styles.why}>{why}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
          {planned.length > 0 && (
            <p className={styles.wake}>
              Each change is recorded as yours and shows at once. The agents take them in on
              their next daily run.
            </p>
          )}
          {error && <p className={styles.error}>{error}</p>}
          <div className={styles.actions}>
            <button type="button" className={styles.cancel} disabled={busy} onClick={onCancel}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.confirm}
              disabled={busy || planned.length === 0}
              onClick={onConfirm}
            >
              {busy
                ? "Saving…"
                : planned.length === 0
                  ? `Nothing to ${verb.toLowerCase()}`
                  : `${verb} ${counted(choice, planned.length)}`}
            </button>
          </div>
        </div>
      )}
    </dialog>
  );
};

/** How a confirmed bulk change went: what changed and when its agents take it in, and any
 * specialist's batch that was refused, with why (its actions stay ticked to retry). */
const BulkResult = ({
  outcomes,
  projectName,
  onDismiss,
}: {
  outcomes: BulkChangeOutcome[];
  projectName: (projectId: string) => string;
  onDismiss: () => void;
}) => {
  const landed = outcomes.filter((o) => o.result);
  const failed = outcomes.filter((o) => !o.result);
  const unchanged = landed.reduce((n, o) => n + (o.result?.unchanged.length ?? 0), 0);
  // A date kept leaves its action as it was.
  const kept = landed.reduce(
    (n, o) => n + o.body.changes.filter((c) => c.change.kind === "keep_move").length,
    0,
  );
  const count =
    landed.reduce((n, o) => n + o.body.changes.length, 0) - unchanged - kept;
  const label = (o: BulkChangeOutcome) => {
    const name = projectName(o.body.project_id);
    return `${agentDisplayLabel(o.body.agent)}${name ? ` on ${name}` : ""}`;
  };
  return (
    <div className={failed.length ? styles.resultBad : styles.result} role="status">
      <p className={styles.resultLine}>
        {count + kept > 0
          ? [
              count > 0 && `${plural(count, "action")} changed.`,
              kept > 0 && `${plural(kept, "date")} kept as ${kept === 1 ? "it was" : "they were"}.`,
              "The agents take them in on their next daily run.",
            ]
              .filter(Boolean)
              .join(" ")
          : "Nothing was changed."}
        {unchanged > 0 && ` ${plural(unchanged, "action")} already had that date.`}
        <button type="button" className={styles.dismiss} onClick={onDismiss}>
          Dismiss
        </button>
      </p>
      {failed.map((o) => (
        <p key={`${o.body.project_id}-${o.body.agent}`} className={styles.resultLine}>
          Not saved for {label(o)} ({plural(o.body.changes.length, "action")}, still ticked):{" "}
          {o.error}
        </p>
      ))}
    </div>
  );
};

/** The bulk actions for a full list: ``rows`` are the open actions ticked and shown. The
 * plan is fixed when a push or resolve is chosen, so a refresh while the confirmation is
 * open cannot change what is sent. ``onDone`` gets the ids still to tick: a refused
 * batch's, to retry. */
export const BulkActions = ({
  rows,
  showProject,
  projectName,
  onDone,
  onClear,
}: {
  rows: BulkRow[];
  showProject: boolean;
  projectName: (projectId: string) => string;
  onDone: (stillTicked: string[]) => void;
  onClear: () => void;
}) => {
  const { user } = useKindeAuth();
  const bulk = useBulkOperatorChange();
  const dismiss = useDismissActions();
  const dismissAll = async () => {
    const byProject = new Map<string, string[]>();
    for (const row of rows)
      byProject.set(row.projectId, [...(byProject.get(row.projectId) ?? []), row.action.action_id]);
    for (const [projectId, actionIds] of byProject)
      await dismiss.mutateAsync({ project_id: projectId, action_ids: actionIds, dismissed: true });
    onClear();
  };
  const [pending, setPending] = useState<{ choice: BulkChoice; plan: BulkPlan } | null>(null);
  const [outcomes, setOutcomes] = useState<BulkChangeOutcome[] | null>(null);
  const empty: BulkPlan = { planned: [], skipped: [] };
  return (
    <>
      {outcomes && (
        <BulkResult
          outcomes={outcomes}
          projectName={projectName}
          onDismiss={() => setOutcomes(null)}
        />
      )}
      {rows.length > 0 && (
        <BulkActionBar
          count={rows.length}
          moves={rows.filter((r) => r.dateMove !== null).length}
          today={rows[0].today}
          busy={bulk.isLoading || dismiss.isLoading}
          onChoose={(choice) => {
            bulk.reset();
            setPending({ choice, plan: planBulk(rows, choice) });
          }}
          onDismiss={() => {
            // The mutation reports its own failure, which leaves the actions ticked to retry.
            dismissAll().catch(() => undefined);
          }}
          onClear={onClear}
        />
      )}
      {dismiss.error && (
        <p className={styles.resultBad} role="status">
          Not dismissed: {dismiss.error.message}
        </p>
      )}
      <BulkActionConfirm
        choice={pending?.choice ?? null}
        plan={pending?.plan ?? empty}
        showProject={showProject}
        busy={bulk.isLoading}
        error={bulk.error?.message ?? null}
        onCancel={() => {
          if (!bulk.isLoading) setPending(null);
        }}
        onConfirm={() => {
          if (!pending) return;
          bulk.mutate(bulkBodies(pending.plan, user?.email ?? ""), {
            onSuccess: (done) => {
              setPending(null);
              setOutcomes(done);
              onDone(
                done
                  .filter((o) => !o.result)
                  .flatMap((o) => o.body.changes.map((c) => c.item_id)),
              );
            },
          });
        }}
      />
    </>
  );
};
